import { FC } from "react";
import { useTranslation } from "react-i18next";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import { trackOnboarding as track } from "~/events";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import {
  FormContainer,
  NextButton,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  RadioInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { requiredString } from "~/utils/validations";

export type HowDidYouHear =
  | ""
  | "Conceive coach"
  | "Conceive member"
  | "Social media"
  | "An article or blog post"
  | "A friend"
  | "Google search"
  | "Clinic or doctor";

type Values = {
  howDidYouHear: HowDidYouHear;
};

const values: HowDidYouHear[] = [
  "Conceive coach",
  "Conceive member",
  "Social media",
  "An article or blog post",
  "A friend",
  "Google search",
  "Clinic or doctor",
];

const validationSchema = Yup.object({
  howDidYouHear: requiredString,
});

export const HowDidYouHearPage: FC = () => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const { howDidYouHear } = useOnboarding();
  const { onSubmit } = useSubmitOnboardingQuestion();
  const isNotOnboarded = !user?.isOnboarded;

  const handleSubmit = (
    values: Values,
    formikHelpers: FormikHelpers<Values>,
  ) => {
    isNotOnboarded && track.submit();
    onSubmit(values, formikHelpers);
  };

  const initialSelected = (values.includes(howDidYouHear as HowDidYouHear)
    ? howDidYouHear
    : "") as HowDidYouHear;

  const initialValues: Values = {
    howDidYouHear: initialSelected,
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema,
    validateOnMount: true,
  });

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt question={t("onboarding.howDidYouHearPage.question")} />

          <RadioInput
            autoAdvance
            defaultValue={initialSelected}
            id="howDidYouHear"
            values={values}
          />
        </QuestionContainer>

        <NextButton showButton={!!howDidYouHear} />
      </FormContainer>
    </QuestionPageContainer>
  );
};
